import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import isEqual from 'lodash.isequal';
import { useCallback, useMemo } from 'react';

import { SetAiExtractionContext } from '#screens/ai-workbench/ai-extraction-context.js';
import { PinoutTable } from '#screens/pinout-table/pinout-table.js';
import { useTRPC } from '#src/trpc.js';
import { useAutoSave } from '#utils/use-auto-save/use-auto-save.js';
import { getPinoutAiExtractionContextValue } from './pinout-ai-extraction-context.js';

export const Route = createFileRoute('/_authorization-gate/package-store/$packageFamilyId/packages/$packageId/pinout/')(
	{
		staticData: {
			navigation: {
				label: 'Package: Pinout',
				keywords: ['pinout', 'pins', 'signals'],
			},
		},
		loader: ({ context: { queryClient, getPackageOptions } }) => {
			void queryClient.prefetchQuery(getPackageOptions);
			const cached = queryClient.getQueryData(getPackageOptions.queryKey);
			return { pkg: cached?.pkg };
		},
		head: ({ loaderData }) => ({
			meta: [
				{
					title:
						(loaderData?.pkg ? loaderData.pkg.manufacturerPartNumber + ': ' : '') +
						'Pinout • Package Editor • Cadstrom',
				},
			],
		}),
		component: PinoutTab,
	},
);

function PinoutTab() {
	const queryClient = useQueryClient();
	const { getPackageOptions } = Route.useRouteContext();
	const trpc = useTRPC();

	const {
		data: { pkg },
	} = useSuspenseQuery(getPackageOptions);

	const { mutateAsync: updatePackage, isPending: updatePackageIsPending } = useMutation(
		trpc.package.update.mutationOptions({
			onSuccess: (res) => {
				// Merge rather than replace: `isConfidential` is derived from the package family's owner and
				// is not in the mutation response, so overwriting the entry would drop it and hide the badge.
				queryClient.setQueryData(getPackageOptions.queryKey, (previous) =>
					previous === undefined ? undefined : { ...previous, pkg: res.pkg },
				);
				setServerPinMap(res.pkg.pinMap);
			},
		}),
	);

	const {
		draftState: draftPinMap,
		setDraftState: setDraftPinMap,
		setServerState: setServerPinMap,
		isDirty,
		handleSave,
		saveAreaContainerRef,
	} = useAutoSave({
		initialState: pkg.pinMap,
		mutationKeys: trpc.package.update.mutationKey(),
		saveFn: async (draftData) => {
			await updatePackage({
				id: pkg.id,
				data: {
					pinMap: { set: draftData },
				},
			});
		},
		equalityFn: (serverData, draftData) => {
			const draftDataWithoutEmpty = Object.fromEntries(
				Object.entries(draftData).filter(([, pinNumbers]) => pinNumbers.length > 0),
			);

			return isEqual(serverData, draftDataWithoutEmpty);
		},
	});

	const handleSetPinMapEntry = useCallback(
		(pkgId: string, signalId: string, pinNumbers: string[]) => {
			setDraftPinMap((prev) => ({
				...prev,
				[signalId]: pinNumbers,
			}));
		},
		[setDraftPinMap],
	);

	const handleUpdatePackagePinMapEntry = useCallback(
		(pinMap: Record<string, string[]>) => {
			setDraftPinMap((prev) => ({
				...prev,
				...pinMap,
			}));
		},
		[setDraftPinMap],
	);

	const handleMergePackagePinMapEntry = useCallback(
		(pinMap: Record<string, string[]>) => {
			setDraftPinMap((prev) => ({
				...prev,
				...Object.entries(pinMap).reduce<Record<string, string[]>>(
					(acc, [signalId, pinNumbers]) => {
						if (acc[signalId]) {
							for (const pinNumber of pinNumbers) {
								if (!acc[signalId].includes(pinNumber)) {
									acc[signalId].push(pinNumber);
								}
							}
						} else {
							acc[signalId] = pinNumbers;
						}
						return acc;
					},
					{ ...prev },
				),
			}));
		},
		[setDraftPinMap],
	);

	const extractionContextValue = useMemo(() => {
		return getPinoutAiExtractionContextValue({
			signals: pkg.signals,
			setPinMap: (pinMap) => handleUpdatePackagePinMapEntry(pinMap),
			mergePinMap: (pinMap) => handleMergePackagePinMapEntry(pinMap),
		});
	}, [pkg, handleUpdatePackagePinMapEntry, handleMergePackagePinMapEntry]);

	return (
		<div ref={saveAreaContainerRef} className='flex min-h-0 grow px-3 py-2'>
			<PinoutTable
				signals={pkg.signals}
				pkgs={[pkg]}
				pinMaps={{ [pkg.id]: draftPinMap }}
				setPinMapEntry={handleSetPinMapEntry}
				showSaveButton={{
					saveDisabled: !isDirty,
					onClickSave: handleSave,
					isSaving: updatePackageIsPending,
				}}
			/>

			<SetAiExtractionContext value={extractionContextValue} />
		</div>
	);
}
